export const REPORT_TARGET_TYPES = ['Post', 'Item', 'User', 'Ads'] as const;
export type ReportTargetType = (typeof REPORT_TARGET_TYPES)[number];

export const REPORT_REASONS = ['Spam', 'Harmful', 'Sexual', 'Hateful', 'Violent', 'Other'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export const REPORT_STATUSES = ['open', 'in_review', 'actioned', 'dismissed'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const MODERATION_ACTIONS = [
  'marked_reviewed',
  'dismissed',
  'content_removed',
  'user_warned',
  'user_banned',
] as const;
export type ModerationAction = (typeof MODERATION_ACTIONS)[number];

export const USER_FACING_REPORT_STATUSES = [
  'received',
  'under_review',
  'action_taken',
  'closed',
  'no_violation_found',
] as const;
export type UserFacingReportStatus = (typeof USER_FACING_REPORT_STATUSES)[number];

export const REPORT_STATUS_GROUPS = {
  pending: ['open', 'pending', 'new'],
  reviewing: ['in_review', 'reviewing', 'under_review'],
  resolved: ['actioned', 'resolved'],
  dismissed: ['dismissed'],
} as const;
export type ReportStatusGroup = keyof typeof REPORT_STATUS_GROUPS;

export const REPORT_PRIORITIES = ['high', 'medium', 'low'] as const;
export type ReportPriority = (typeof REPORT_PRIORITIES)[number];

export const REPORT_SORT_MODES = ['newest', 'oldest', 'priority', 'reports'] as const;
export type ReportSortMode = (typeof REPORT_SORT_MODES)[number];

export type ReportBadgeState = 'pending' | 'under_review' | 'action_taken' | 'dismissed' | 'escalated';

export interface ReportBadgeStateConfig {
  id: ReportBadgeState;
  label: string;
  color: 'warning' | 'primary' | 'success' | 'medium' | 'danger';
  icon: string;
  description: string;
}

export const REPORT_BADGE_STATES: ReportBadgeStateConfig[] = [
  { id: 'pending', label: 'Pending', color: 'warning', icon: 'time', description: 'Awaiting review' },
  { id: 'under_review', label: 'Under Review', color: 'primary', icon: 'search', description: 'Being investigated' },
  {
    id: 'action_taken',
    label: 'Action Taken',
    color: 'success',
    icon: 'shield-checkmark',
    description: 'Enforcement applied',
  },
  { id: 'dismissed', label: 'Dismissed', color: 'medium', icon: 'close-circle', description: 'No action required' },
  {
    id: 'escalated',
    label: 'Escalated',
    color: 'danger',
    icon: 'arrow-up-circle',
    description: 'Requires higher authority',
  },
];

export type ReportBadgePriority = 'low' | 'medium' | 'high' | 'critical';

export interface ReportBadgePriorityConfig {
  id: ReportBadgePriority;
  label: string;
  color: 'medium' | 'primary' | 'warning' | 'danger';
  reviewSlaHours: number;
}

export const REPORT_BADGE_PRIORITIES: ReportBadgePriorityConfig[] = [
  { id: 'low', label: 'Low', color: 'medium', reviewSlaHours: 72 },
  { id: 'medium', label: 'Medium', color: 'primary', reviewSlaHours: 48 },
  { id: 'high', label: 'High', color: 'warning', reviewSlaHours: 24 },
  { id: 'critical', label: 'Critical', color: 'danger', reviewSlaHours: 4 },
];

export function getReportBadgeStateConfig(state: ReportBadgeState): ReportBadgeStateConfig {
  return REPORT_BADGE_STATES.find((item) => item.id === state) || REPORT_BADGE_STATES[0];
}

export function getReportBadgePriorityConfig(priority: ReportBadgePriority): ReportBadgePriorityConfig {
  return REPORT_BADGE_PRIORITIES.find((item) => item.id === priority) || REPORT_BADGE_PRIORITIES[0];
}
